import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, Download, Stethoscope, Siren, Copy, ExternalLink, Info, HeartPulse, Lock } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

const QR_KINDS = [
  {
    id: "bilan",
    path: "bilan-prevention",
    label: "Bilan prévention",
    place: "À placer à l'intérieur (accueil / salle d'attente)",
    desc: "Le patient répond à quelques questions de prévention, puis se présente au pharmacien pour son bilan personnalisé (avis humain).",
    icon: HeartPulse,
    color: "text-violet-600",
    bg: "bg-violet-500/10",
  },
  {
    id: "teleconsultation",
    path: "teleconsultation-specialiste",
    label: "Téléconsultation spécialiste en pharmacie",
    place: "À placer à l'intérieur, au-dessus du guichet",
    desc: "Le patient choisit son spécialiste, réserve une cabine, puis prend son rendez-vous sur Doctolib/Qare à l'heure de la cabine.",
    icon: Stethoscope,
    color: "text-primary",
    bg: "bg-primary/10",
  },
  {
    id: "urgences",
    path: "services-urgences",
    label: "Services d'urgences",
    place: "À placer en vitrine / à l'extérieur (accès quand la pharmacie est fermée)",
    desc: "Accès direct aux centres de soins ouverts à proximité et aux téléconsultations généralistes rapides. Sans bilan ni cabine.",
    icon: Siren,
    color: "text-rose-600",
    bg: "bg-rose-500/10",
  },
  {
    id: "personnel",
    fixedPath: "/login",
    label: "Vous faites partie du personnel ?",
    place: "Réservé à l'équipe — ne pas afficher au public",
    desc: "Accès à l'espace officine : connexion, gestion des cabines et du calendrier de disponibilité.",
    icon: Lock,
    color: "text-slate-800",
    bg: "bg-slate-800/10",
  },
];

function QrCard({ kind, slug, pharmacyName }) {
  const ref = useRef(null);
  const url = kind.fixedPath ? `${BASE}${kind.fixedPath}` : `${BASE}/p/${slug}/${kind.path}`;
  const Icon = kind.icon;

  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const pad = 48;
    const out = document.createElement("canvas");
    out.width = canvas.width + pad * 2;
    out.height = canvas.height + pad * 2 + 70;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, pad, pad);
    ctx.fillStyle = "#064e3b";
    ctx.font = "bold 26px Helvetica, Arial";
    ctx.textAlign = "center";
    ctx.fillText(kind.label, out.width / 2, canvas.height + pad + 42);
    ctx.fillStyle = "#555";
    ctx.font = "16px Helvetica, Arial";
    ctx.fillText(pharmacyName || "", out.width / 2, canvas.height + pad + 66);
    const a = document.createElement("a");
    a.href = out.toDataURL("image/png");
    a.download = `qr-${kind.fixedPath ? kind.id : `${slug}-${kind.path}`}.png`;
    a.click();
    toast.success("QR code téléchargé.");
  };

  return (
    <Card className="rounded-2xl p-6" data-testid={`qr-card-${kind.id}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`h-11 w-11 rounded-xl ${kind.bg} grid place-items-center ${kind.color} shrink-0`}><Icon className="h-6 w-6" /></div>
        <div>
          <h2 className="font-heading font-bold leading-tight">{kind.label}</h2>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mt-0.5">{kind.place}</p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-5">{kind.desc}</p>
      <div className="flex justify-center mb-5">
        <div ref={ref} className="rounded-2xl border-2 border-dashed p-4 bg-white">
          <QRCodeCanvas value={url} size={200} level="M" includeMargin={false} fgColor="#064e3b" />
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-muted/50 px-3 py-2 mb-4">
        <code className="flex-1 text-[11px] break-all text-muted-foreground" data-testid={`qr-url-${kind.id}`}>{url}</code>
        <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Lien copié"); }} className="text-muted-foreground hover:text-foreground" aria-label="Copier le lien"><Copy className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button data-testid={`qr-download-${kind.id}`} onClick={download} className="h-12 rounded-xl gap-2"><Download className="h-4 w-4" /> Télécharger</Button>
        <a href={url} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="w-full h-12 rounded-xl gap-2"><ExternalLink className="h-4 w-4" /> Tester</Button></a>
      </div>
    </Card>
  );
}

export default function QrCodesAdmin() {
  const [pharmacies, setPharmacies] = useState([]);
  const [slug, setSlug] = useState(null);

  useEffect(() => {
    api.get("/admin/pharmacies").then((r) => {
      const list = r.data.pharmacies || [];
      setPharmacies(list);
      if (list[0]) setSlug(list[0].slug);
    }).catch(() => toast.error("Impossible de charger les pharmacies."));
  }, []);

  const current = pharmacies.find((p) => p.slug === slug);

  return (
    <div className="min-h-screen bg-background">
      <StaffHeader />
      <main className="mx-auto max-w-4xl px-4 py-8">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-primary-foreground"><QrCode className="h-5 w-5" /></div>
          <div><h1 className="font-heading text-2xl font-bold leading-tight">QR Codes</h1>
            <p className="text-[11px] text-muted-foreground">Générez et imprimez les QR codes du parcours patient</p></div>
        </div>

        <Card className="rounded-2xl p-4 my-6 flex items-center gap-3">
          <span className="text-sm font-medium shrink-0">Pharmacie</span>
          <Select value={slug || ""} onValueChange={setSlug}>
            <SelectTrigger data-testid="qr-pharmacy-select" className="h-11 rounded-xl max-w-xs"><SelectValue placeholder="Choisir une pharmacie" /></SelectTrigger>
            <SelectContent>
              {pharmacies.map((p) => (
                <SelectItem key={p.slug} value={p.slug} data-testid={`qr-pharmacy-${p.slug}`}>{p.name} — {p.city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Card>

        {slug && (
          <div className="grid md:grid-cols-2 gap-5" data-testid="qr-grid">
            {QR_KINDS.map((k) => <QrCard key={k.id} kind={k} slug={slug} pharmacyName={current?.name} />)}
          </div>
        )}

        <Card className="rounded-xl p-4 mt-6 text-xs text-muted-foreground flex gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Imprimez ces QR codes et placez-les selon l'usage indiqué. Les liens sont propres à chaque pharmacie (multi-officines pris en charge).</span>
        </Card>
      </main>
    </div>
  );
}
